import { useCallback, useRef, useState } from "react";
import { CellState } from "../types";
import { Cell } from "./Cell";
import { decideSwipe, isFlick } from "../lib/swipe";
import styles from "./Grid.module.css";

interface GridProps {
  cellStates: CellState[][];
  onCellClick: (row: number, col: number) => void;
  onSwap?: (fromRow: number, fromCol: number, toRow: number, toCol: number) => void;
  isCellSwappable?: (row: number, col: number) => boolean;
  /** Fired on a horizontal touch/pen swipe that did not move a tile. */
  onSwipe?: (direction: "prev" | "next") => void;
}

const DRAG_THRESHOLD = 8; // px before drag activates

interface DragState {
  fromRow: number;
  fromCol: number;
  letter: string;
  startX: number;
  startY: number;
  active: boolean;
}

export function Grid({ cellStates, onCellClick, onSwap, isCellSwappable, onSwipe }: GridProps) {
  const rows = cellStates.length;
  const cols = cellStates[0]?.length ?? 0;
  const gridRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const swipeRef = useRef<{ x: number; y: number; type: string; t: number } | null>(null);
  const suppressClickRef = useRef(false);
  const [dragFrom, setDragFrom] = useState<{ row: number; col: number } | null>(null);
  const [dragOver, setDragOver] = useState<{ row: number; col: number } | null>(null);

  const getCellAtPoint = useCallback(
    (x: number, y: number): { row: number; col: number } | null => {
      const grid = gridRef.current;
      if (!grid) return null;
      const rect = grid.getBoundingClientRect();
      const relX = x - rect.left;
      const relY = y - rect.top;
      if (relX < 0 || relY < 0 || relX > rect.width || relY > rect.height) return null;
      const col = Math.floor((relX / rect.width) * cols);
      const row = Math.floor((relY / rect.height) * rows);
      if (row < 0 || row >= rows || col < 0 || col >= cols) return null;
      return { row, col };
    },
    [rows, cols]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // The grid owns all gestures within its bounds (tile drag + flick-to-
      // navigate). Stop propagation so an ancestor swipe surface (the game
      // container) doesn't also process board gestures and double-navigate.
      e.stopPropagation();

      // Track every touch/pen press for swipe navigation, regardless of whether
      // it lands on a swappable tile (a swipe over locked cells or gaps should
      // still navigate). Tile-drag detection below takes precedence at release.
      swipeRef.current =
        onSwipe && (e.pointerType === "touch" || e.pointerType === "pen")
          ? { x: e.clientX, y: e.clientY, type: e.pointerType, t: performance.now() }
          : null;

      if (!onSwap || !isCellSwappable) return;
      if (e.button !== 0) return;
      const target = getCellAtPoint(e.clientX, e.clientY);
      if (!target) return;
      const cell = cellStates[target.row]?.[target.col];
      if (!cell || cell.letter === null || cell.isLocked) return;
      if (!isCellSwappable(target.row, target.col)) return;

      dragRef.current = {
        fromRow: target.row,
        fromCol: target.col,
        letter: cell.letter,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
      };
    },
    [onSwap, isCellSwappable, cellStates, getCellAtPoint, onSwipe]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      if (!drag.active) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        drag.active = true;
        // Capture pointer once tracking starts for reliable tracking
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }

      e.preventDefault();

      // While the gesture reads as a fast horizontal flick, keep the tile drag
      // invisible (no ghost, no drop highlight) — it will navigate on release,
      // so nothing should visibly move. Slower or vertical gestures reveal the
      // ghost and behave as a normal tile drag. (Tracking stays active either
      // way so a short/slow drag still swaps on release.)
      const sw = swipeRef.current;
      const dt = sw ? performance.now() - sw.t : Infinity;
      const flicking = !!sw && Math.abs(dx) > Math.abs(dy) && isFlick(dx, dt);
      if (flicking) {
        setDragFrom(null);
        setDragOver(null);
        return;
      }
      setDragFrom({ row: drag.fromRow, col: drag.fromCol });

      // Position ghost
      const ghost = ghostRef.current;
      if (ghost) {
        ghost.style.left = `${e.clientX}px`;
        ghost.style.top = `${e.clientY}px`;
      }

      // Determine drop target using grid geometry (works with pointer capture)
      const target = getCellAtPoint(e.clientX, e.clientY);

      if (target && isCellSwappable && isCellSwappable(target.row, target.col) &&
          !(target.row === drag.fromRow && target.col === drag.fromCol)) {
        const targetCell = cellStates[target.row]?.[target.col];
        if (targetCell && targetCell.letter !== drag.letter) {
          setDragOver(target);
        } else {
          setDragOver(null);
        }
      } else {
        setDragOver(null);
      }
    },
    [getCellAtPoint, isCellSwappable, cellStates]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      dragRef.current = null;

      // Navigation swipe. A qualifying swipe navigates when no tile was grabbed;
      // when a tile drag is active, only a fast horizontal flick overrides it, so
      // deliberate (slower, aimed) drags still swap tiles.
      const swipe = swipeRef.current;
      swipeRef.current = null;
      const movedTile = !!(drag && drag.active);
      if (swipe && onSwipe) {
        const dir = decideSwipe(
          e.clientX - swipe.x,
          e.clientY - swipe.y,
          performance.now() - swipe.t,
          movedTile,
        );
        if (dir) {
          setDragFrom(null);
          setDragOver(null);
          onSwipe(dir);
          return; // overrides any pending tile swap
        }
      }

      if (!drag || !drag.active) {
        setDragFrom(null);
        setDragOver(null);
        return;
      }

      // Resolve drop target from final position
      const dropTarget = getCellAtPoint(e.clientX, e.clientY);
      setDragFrom(null);
      setDragOver(null);

      if (dropTarget && onSwap &&
          !(dropTarget.row === drag.fromRow && dropTarget.col === drag.fromCol)) {
        onSwap(drag.fromRow, drag.fromCol, dropTarget.row, dropTarget.col);
      }

      // Suppress the next click event so no cell gets selected after drag
      suppressClickRef.current = true;
    },
    [getCellAtPoint, onSwap, onSwipe]
  );

  const handlePointerCancel = useCallback(() => {
    dragRef.current = null;
    swipeRef.current = null;
    setDragFrom(null);
    setDragOver(null);
  }, []);

  const handleClick = useCallback(
    (row: number, col: number) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      onCellClick(row, col);
    },
    [onCellClick]
  );

  return (
    <div
      ref={gridRef}
      className={styles.grid}
      style={
        {
          "--grid-cols": cols,
        } as React.CSSProperties
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {cellStates.map((row) =>
        row.map((cell) => (
          <Cell
            key={`${cell.row}-${cell.col}`}
            cell={cell}
            onClick={handleClick}
            isDragSource={dragFrom !== null && dragFrom.row === cell.row && dragFrom.col === cell.col}
            isDragTarget={dragOver !== null && dragOver.row === cell.row && dragOver.col === cell.col}
          />
        ))
      )}
      {dragFrom !== null && (
        <div
          ref={ghostRef}
          className={styles.ghost}
        >
          {dragRef.current?.letter.toUpperCase()}
        </div>
      )}
    </div>
  );
}
